import React, { useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { toastr } from 'react-redux-toastr';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Grid, Typography, Box, CircularProgress } from '@material-ui/core';
import { uniqueId } from 'lodash';
import { filesize } from 'filesize';

import TituloPagina from '../../components/layout/TituloPagina';
import FileList from '../../components/templates/fileList/FileList';

import {
    carregarUploadedLogo,
    getNegocio,
    deleteImagemLogo,
    addImagemLogo
} from '../../store/actions/negocio';

import {
    Container,
    Content,
    DropContainer,
    UploadMessage
} from './styles';

const MAX_SIZE = 500000; // 500kb
const MAX_FILES = 1;

const NegocioUploadLogo = props => {

    const history = useHistory();
    const location = useLocation();
    const {
        getNegocio,
        negocio,
        uploadedLogo,
        carregarUploadedLogo,
        deleteImagemLogo,
        addImagemLogo,
        loading
    } = props;

    useEffect(() => {
        const id = Number(location?.state?.lojaId);
        const isEditRoute = location.pathname === '/lojas/logo-da-loja';

        if (isEditRoute && !id) {
            history.replace('/lojas');
            return;
        }

        if (id > 0) {
            getNegocio(id);
        }
    }, [getNegocio, history, location]);

    useEffect(() => {
        handleExistingImages();
    }, [negocio]);

    const renderDragMessage = (isDragActive, isDragReject) => {
        if (!isDragActive) {
            return <UploadMessage>Arraste seu logotipo para este espaço.</UploadMessage>;
        }
        if (isDragReject) {
            return <UploadMessage type="error">Arquivo não suportado!</UploadMessage>;
        }
        return <UploadMessage type="success">Pode soltar o arquivo!</UploadMessage>;
    }

    const handleExistingImages = () => {
        const { id, logotipo } = negocio;

        if (!logotipo) return;

        const savedFiles = [{
            id: id,
            readableSize: null,
            preview: logotipo,
            progress: 0,
            uploaded: true,
            error: false,
            url: logotipo,
        }];

        carregarUploadedLogo(savedFiles);
    }

    const validateFile = (file) => {
        if (!['image/png', 'image/jpeg'].includes(file.type)) {
            toastr.error('Tipo inválido', `O arquivo "${file.name}" não está no formato PNG ou JPG.`);
            return false;
        }

        if (file.size > MAX_SIZE) {
            toastr.error('Arquivo muito grande', `A imagem "${file.name}" excede 500Kb.`);
            return false;
        }

        return true;
    }

    const handleUpload = (files) => {

        if (uploadedLogo.length >= MAX_FILES) {
            toastr.error('Erro', 'Seu logotipo já foi enviado. Se quiser trocá-lo, basta excluir o atual e fazer o upload de um novo arquivo.');
            return;
        }

        const validFiles = files.filter(validateFile);

        if (validFiles.length === 0) return;

        const remainingSlots = MAX_FILES - uploadedLogo.length;
        const filesToUpload = validFiles.slice(0, remainingSlots);

        if (validFiles.length > remainingSlots) {
            toastr.warning('Limite atingido', 'Algumas imagens foram ignoradas por exceder o limite de 1 imagem.');
        }

        const idTemp = uniqueId();

        const uploadingFiles = filesToUpload.map(file => ({
            file,
            idTemp,
            readableSize: filesize(file.size),
            preview: URL.createObjectURL(file),
            progress: 0,
            uploaded: false,
            error: false,
            url: null,
            id: negocio.id
        }));

        carregarUploadedLogo([...uploadedLogo, ...uploadingFiles]);

        uploadingFiles.forEach(file => addImagemLogo(file));
    }

    const handleDelete = (id) => {
        deleteImagemLogo(id);
        getNegocio(id);
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, height: '70vh' }}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    return (
        <>
            <TituloPagina titulo={`Cadastro de Logotipo de ${negocio.nome}`} />

            <Grid container item xs={12} style={{ paddingTop: '15px' }}>
                <Grid container>
                    <Typography variant="body1" component="p">
                        Selecione ou arraste o arquivo do logotipo para o espaço abaixo. A imagem deve ser em formato PNG ou JPG e ter, no máximo, 500Kb.
                        Para uma melhor apresentação da sua marca, sugerimos que ela esteja centralizada na imagem e, preferencialmente, com fundo branco.
                    </Typography>
                </Grid>
            </Grid>

            <Container>
                <Content>
                    <Dropzone
                        accept={{ 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] }}
                        onDrop={handleUpload}
                        multiple
                    >
                        {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
                            <DropContainer
                                {...getRootProps()}
                                isDragActive={isDragActive}
                                isDragReject={isDragReject}
                            >
                                <input {...getInputProps()} />
                                {renderDragMessage(isDragActive, isDragReject)}
                            </DropContainer>
                        )}
                    </Dropzone>

                    {!!uploadedLogo.length && (
                        <FileList
                            files={uploadedLogo}
                            onDelete={handleDelete}
                        />
                    )}
                </Content>
            </Container>
            <Grid container>
                <Button variant='contained' color='secondary' onClick={() => window.history.back()}>Finalizar</Button>
            </Grid>
        </>
    );
};

const mapStateToProps = state => ({
    negocio: state.negocio.negocio,
    uploadedLogo: state.negocio.uploadedLogo,
    loading: state.negocio.loading
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getNegocio,
            carregarUploadedLogo,
            addImagemLogo,
            deleteImagemLogo
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(NegocioUploadLogo);